import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Box, Button, CircularProgress, Stack, Step, StepLabel, Stepper } from '@mui/material';
import { ArrowBack as ArrowBackIcon, GroupAddOutlined as GroupAddIcon } from '@mui/icons-material';
import { userAPI } from '../../API/userAPI';
import { gatewayContagemAPI } from '../../API/gatewayContagemAPI';
import { getInvitableProfileCodes, NOTIF_CATEGORIES } from '../../Constants/userConstants';
import ModalTemplate from './ModalTemplate';
import CreateUsersTable, { emptyBulkRow, DEFAULT_BULK_ROWS, isBulkValid, rowNeedsClient, rowNeedsStores, getRowProfileCode } from '../Tables/CreateUsersTable';
import BulkClientStoresStep, { isClientStepValid } from '../User/Steps/BulkClientStoresStep';
import TypeStep from '../User/Steps/TypeStep';
import InfoStep from '../User/Steps/InfoStep';
import ClientStoresStep from '../User/Steps/ClientStoresStep';

const defaultBulkRows = (defaultClientId) => Array.from({ length: DEFAULT_BULK_ROWS }, () => emptyBulkRow(defaultClientId));

// eslint-disable-next-line react/prop-types
const InviteUserDialog = ({ open, onClose, onSuccess, currentUserRole, currentUserClientId }) => {
  const { t } = useTranslation('users');
  const isGatewayAdmin = currentUserRole === 'GATEWAY_ADMIN';
  const isClientAdminCaller = currentUserRole === 'CLIENT_ADMIN';

  const [activeStep, setActiveStep] = useState(0);
  const [inviteType, setInviteType] = useState(isGatewayAdmin ? null : 'local');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [profileKey, setProfileKey] = useState('');

  const [clientId, setClientId] = useState('');
  const [storeIds, setStoreIds] = useState([]);
  const [clients, setClients] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [stores, setStores] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [bulkMode, setBulkMode] = useState(false);
  const [bulkRows, setBulkRows] = useState(defaultBulkRows(isClientAdminCaller ? currentUserClientId : ''));
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({});

  const profileCodeLabel = (code) => {
    if (code === 'GATEWAY_ADMIN') return t('profiles.gatewayAdmin');
    if (code === 'CLIENT_ADMIN') return t('profiles.clientAdmin');
    return t('profiles.user');
  };

  const profileOptionsFor = (type) => {
    const codes = getInvitableProfileCodes(currentUserRole, type);
    return profiles
      .filter((p) => codes.includes(p.code))
      .map((p) => ({ value: p.id, label: profileCodeLabel(p.code), code: p.code }));
  };

  const profileOptions = profileOptionsFor(inviteType);

  const selectedProfileCode = profiles.find((p) => p.id === profileKey)?.code ?? '';
  const needsClientStep = selectedProfileCode === 'CLIENT_ADMIN' || selectedProfileCode === 'USER';
  const needsStoresPicker = selectedProfileCode === 'USER';
  const notifCategories = NOTIF_CATEGORIES[selectedProfileCode] ?? [];

  const typeStepIdx = isGatewayAdmin ? 0 : null;
  const infoStepIdx = isGatewayAdmin ? 1 : 0;
  const clientStepIdx = isGatewayAdmin ? 2 : 1;

  const bulkNeedsClientStep = bulkRows.some((row) => rowNeedsClient(row, profileOptions));

  const effectiveSteps = bulkMode
    ? (bulkNeedsClientStep
        ? (isGatewayAdmin ? [t('invite.stepType'), t('invite.stepInfo'), t('invite.stepClient')] : [t('invite.stepInfo'), t('invite.stepClient')])
        : (isGatewayAdmin ? [t('invite.stepType'), t('invite.stepInfo')] : [t('invite.stepInfo')]))
    : (needsClientStep
        ? (isGatewayAdmin ? [t('invite.stepType'), t('invite.stepInfo'), t('invite.stepClient')] : [t('invite.stepInfo'), t('invite.stepClient')])
        : (isGatewayAdmin ? [t('invite.stepType'), t('invite.stepInfo')] : [t('invite.stepInfo')]));

  useEffect(() => {
    if (!open) return;
    setLoadingClients(true);
    Promise.all([gatewayContagemAPI.listClients(), userAPI.listProfiles()])
      .then(([clientsRes, profilesRes]) => {
        setClients(Array.isArray(clientsRes?.value?.items) ? clientsRes.value.items : []);
        setProfiles(Array.isArray(profilesRes?.value?.items) ? profilesRes.value.items : []);
      })
      .catch(() => { setClients([]); setProfiles([]); })
      .finally(() => setLoadingClients(false));
  }, [open]);

  useEffect(() => {
    if (!clientId) { setStores([]); setStoreIds([]); return; }
    setLoadingStores(true);
    gatewayContagemAPI.listStoresByClient(clientId)
      .then((res) => setStores(Array.isArray(res?.value) ? res.value : []))
      .catch(() => setStores([]))
      .finally(() => setLoadingStores(false));
  }, [clientId]);

  const handleClose = () => {
    setActiveStep(0);
    setInviteType(isGatewayAdmin ? null : 'local');
    setName(''); setEmail(''); setPhone(''); setJobTitle('');
    setProfileKey('');
    setClientId(''); setStoreIds([]); setStores([]);
    setBulkMode(false);
    setBulkRows(defaultBulkRows(isClientAdminCaller ? currentUserClientId : ''));
    setAttemptedSubmit(false);
    setNotifPrefs({});
    onClose();
  };

  const startBulkMode = () => {
    setBulkMode(true);
    setBulkRows(defaultBulkRows(isClientAdminCaller ? currentUserClientId : ''));
    setAttemptedSubmit(false);
  };

  const exitBulkMode = () => {
    setBulkMode(false);
    setBulkRows(defaultBulkRows(isClientAdminCaller ? currentUserClientId : ''));
    setAttemptedSubmit(false);
  };

  const handleProfileKeyChange = (value) => {
    setProfileKey(value);
    const code = profiles.find((p) => p.id === value)?.code ?? '';
    const needsClient = code === 'CLIENT_ADMIN' || code === 'USER';
    setClientId(isClientAdminCaller && needsClient ? currentUserClientId : '');
    setStoreIds([]);
    setNotifPrefs({});
  };

  const handleNotifPrefChange = (category, value) => {
    setNotifPrefs((prev) => ({ ...prev, [category]: value }));
  };

  const handleStoreToggle = (id) => {
    setStoreIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const handleToggleAllStores = () => {
    setStoreIds((prev) => (prev.length === 0 ? stores.map((s) => s.id) : []));
  };

  const canNext = () => {
    if (activeStep === typeStepIdx) return !!inviteType;
    if (activeStep === infoStepIdx) {
      return bulkMode
        ? isBulkValid(bulkRows, profileOptions)
        : !!(name.trim() && email.trim() && profileKey);
    }
    if (activeStep === clientStepIdx) {
      return bulkMode ? isClientStepValid(bulkRows, profileOptions) : !!clientId;
    }
    return true;
  };

  const isLastStep = activeStep === effectiveSteps.length - 1;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const prefsPayload = notifCategories.map((cat) => ({ category: cat, isEnabled: !!notifPrefs[cat] }));
      let result;
      if (inviteType === 'entra') {
        result = await userAPI.inviteEntraUser({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
          jobTitle: jobTitle.trim() || null,
          clientId: needsClientStep ? clientId : null,
          profileIds: profileKey ? [profileKey] : [],
          notificationPreferences: prefsPayload,
        });
      } else {
        result = await userAPI.inviteLocalUser({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
          jobTitle: jobTitle.trim() || null,
          clientId: needsClientStep ? clientId : null,
          storeIds: needsStoresPicker ? storeIds : [],
          profileIds: profileKey ? [profileKey] : [],
          notificationPreferences: prefsPayload,
        });
      }
      if (result?.error) throw new Error(result.description ?? t('invite.error'));

      toast.success(t('invite.success', { email: email.trim() }));
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(err.message ?? t('invite.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkSubmit = async () => {
    setAttemptedSubmit(true);
    setSubmitting(true);
    try {
      const pending = bulkRows.filter((r) => !r.sent);
      const payloadRows = pending.map((r) => {
        const rowNotifCategories = NOTIF_CATEGORIES[getRowProfileCode(r, profileOptions)] ?? [];
        return {
          name: r.name.trim(),
          email: r.email.trim(),
          phone: r.phone.trim() || null,
          jobTitle: r.jobTitle.trim() || null,
          clientId: rowNeedsClient(r, profileOptions) ? r.clientId : null,
          storeIds: rowNeedsStores(r, profileOptions) ? r.storeIds : [],
          profileIds: r.profileId ? [r.profileId] : [],
          notificationPreferences: rowNotifCategories.map((cat) => ({
            category: cat,
            isEnabled: !!r.notifPrefs?.[cat],
          })),
        };
      });

      const result = inviteType === 'entra'
        ? await userAPI.bulkInviteEntraUsers(payloadRows)
        : await userAPI.bulkInviteLocalUsers(payloadRows);

      if (result?.error) throw new Error(result.description ?? t('invite.error'));

      const apiResults = result.value?.results ?? [];
      const byEmail = Object.fromEntries(apiResults.map((r) => [r.email.toLowerCase(), r]));

      const updated = bulkRows.map((row) => {
        if (row.sent) return row;
        const rowResult = byEmail[row.email.trim().toLowerCase()];
        if (!rowResult) return row;
        return rowResult.success
          ? { ...row, sent: true, serverError: '' }
          : { ...row, serverError: rowResult.errorMessage ?? t('invite.error') };
      });

      setBulkRows(updated);

      const successCount = updated.filter((r) => r.sent).length;
      const failCount = updated.length - successCount;

      if (failCount === 0) {
        toast.success(t('invite.bulkSuccess', { count: successCount }));
        onSuccess?.();
        handleClose();
      } else {
        toast.error(t('invite.bulkPartialSuccess', { success: successCount, failed: failCount }));
      }
    } catch (err) {
      toast.error(err.message ?? t('invite.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const hasFailedRows = bulkRows.some((r) => r.serverError);

  const stepperContent = (
    <>
      <Stepper activeStep={activeStep} sx={{ mb: 3, px: 1, '& .MuiStepLabel-label': { fontSize: 12 } }}>
        {effectiveSteps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === typeStepIdx && typeStepIdx !== null && (
        <TypeStep inviteType={inviteType} onSelect={setInviteType} />
      )}

      {activeStep === infoStepIdx && !bulkMode && (
        <Stack spacing={2}>
          <InfoStep
            name={name} onNameChange={setName}
            email={email} onEmailChange={setEmail}
            jobTitle={jobTitle} onJobTitleChange={setJobTitle}
            phone={phone} onPhoneChange={setPhone}
            profileKey={profileKey} onProfileChange={handleProfileKeyChange}
            profileOptions={profileOptions}
            inviteType={inviteType}
            notifPrefs={notifPrefs}
            onNotifPrefChange={handleNotifPrefChange}
          />
          <Box
            onClick={startBulkMode}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 'fit-content', cursor: 'pointer', color: 'primary.main', fontSize: 13, fontWeight: 600 }}
          >
            <GroupAddIcon sx={{ fontSize: 16 }} />
            {t('invite.bulkToggle')}
          </Box>
        </Stack>
      )}

      {activeStep === infoStepIdx && bulkMode && (
        <Stack spacing={2}>
          <CreateUsersTable
            rows={bulkRows}
            onRowsChange={setBulkRows}
            profileOptions={profileOptions}
            attemptedSubmit={attemptedSubmit}
            defaultClientId={isClientAdminCaller ? currentUserClientId : ''}
          />
          <Box
            onClick={exitBulkMode}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 'fit-content', cursor: 'pointer', color: 'primary.main', fontSize: 13, fontWeight: 600 }}
          >
            <ArrowBackIcon sx={{ fontSize: 14 }} />
            {t('invite.bulkBackToggle')}
          </Box>
        </Stack>
      )}

      {activeStep === clientStepIdx && !bulkMode && needsClientStep && (
        <ClientStoresStep
          clientId={clientId} onClientChange={setClientId}
          clients={clients} loadingClients={loadingClients}
          stores={stores} loadingStores={loadingStores}
          needsStoresPicker={needsStoresPicker}
          storeIds={storeIds} onStoreToggle={handleStoreToggle}
          onToggleAllStores={handleToggleAllStores}
          isClientAdminCaller={isClientAdminCaller}
        />
      )}

      {activeStep === clientStepIdx && bulkMode && (
        <BulkClientStoresStep
          rows={bulkRows}
          onRowsChange={setBulkRows}
          profileOptions={profileOptions}
          clients={clients}
          attemptedSubmit={attemptedSubmit}
          isClientAdminCaller={isClientAdminCaller}
        />
      )}
    </>
  );

  const footerActions = (
    <>
      <Button size="small" onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none', fontWeight: 500, color: 'text.secondary' }}>
        {t('invite.actions.cancel')}
      </Button>
      {activeStep > 0 && (
        <Button size="small" onClick={() => setActiveStep((s) => s - 1)} disabled={submitting} sx={{ textTransform: 'none', fontWeight: 500 }}>
          {t('invite.back')}
        </Button>
      )}
      {!isLastStep ? (
        <Button size="small" variant="contained" disabled={!canNext()} onClick={() => setActiveStep((s) => s + 1)} sx={{ textTransform: 'none', fontWeight: 600 }}>
          {t('invite.next')}
        </Button>
      ) : (
        <Button
          size="small"
          variant="contained"
          disabled={!canNext() || submitting}
          onClick={bulkMode ? handleBulkSubmit : handleSubmit}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {bulkMode && hasFailedRows ? t('invite.bulkResend') : t('invite.actions.send')}
        </Button>
      )}
    </>
  );

  return (
    <ModalTemplate
      open={open}
      onClose={handleClose}
      title={t('invite.title')}
      size="md"
      isSubmitting={submitting}
      content={stepperContent}
      customActions={footerActions}
    />
  );
};

export default InviteUserDialog;
